// Turning a product audit entry into "what changed" lines (POS comments item 4).
//
// The admin form sends every field on save, so `changes` (the request body)
// lists far more than what moved; a field is shown only when its old and new
// values differ. Entries recorded before before-values existed have no
// old_value — those say the details were not recorded rather than listing
// every field the form sent.

export interface ChangeLine {
  field: string;
  from: string | null;
  to: string;
}

interface Entry {
  path: string;
  action: string;
  changes: Record<string, unknown> | null;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
}

const LABELS: Record<string, string> = {
  name: 'Name',
  barcode: 'Barcode',
  category: 'Category',
  unit: 'Unit',
  price: 'Selling price',
  cost: 'Cost',
  gst_rate: 'Tax rate',
  purchase_gst_rate: 'Purchase tax rate',
  hsn_code: 'HSN code',
  brand: 'Brand',
  product_type: 'Type',
  low_stock_threshold: 'Minimum stock',
  commission_pct: 'Commission',
  is_active: 'Active',
  image_url: 'Image',
  hide_in_pos: 'Hidden in POS',
  hide_in_sales: 'Hidden in Sales',
};
const MONEY = new Set(['price', 'cost']);
const PERCENT = new Set(['gst_rate', 'purchase_gst_rate', 'commission_pct']);

/** Created / Added variants / Deactivated / Edited, from the route the entry was made on. */
export function changeKind(entry: Pick<Entry, 'path' | 'action'>): 'Created' | 'Added variants' | 'Deactivated' | 'Edited' {
  if (/\/variants\/?$/.test(entry.path)) return 'Added variants';
  if (entry.action.endsWith('.delete')) return 'Deactivated';
  if (entry.action.endsWith('.post')) return 'Created';
  return 'Edited';
}

function show(field: string, value: unknown, formatMoney: (minor: number) => string): string {
  if (value === null || value === undefined || value === '') return '—';
  if (MONEY.has(field)) return formatMoney(Number(value));
  if (PERCENT.has(field)) return `${Number(value)}%`;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (field === 'image_url') return 'changed';
  return String(value);
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
  || (a != null && b != null && !Number.isNaN(Number(a)) && Number(a) === Number(b));

/**
 * The fields an edit actually changed, old → new. Null when the entry has no
 * before-value (an edit made before they were recorded).
 */
export function describeChanges(entry: Entry, formatMoney: (minor: number) => string): ChangeLine[] | null {
  if (changeKind(entry) !== 'Edited') return [];
  if (!entry.old_value) return null;
  const old = entry.old_value;
  const now = entry.new_value ?? {};
  const sent = Object.keys(entry.changes ?? {});
  // A variant's price is sent as price_adjustment; show it as the selling price.
  const fields = new Set(sent.map((k) => (k === 'price_adjustment' ? 'price' : k)));
  const lines: ChangeLine[] = [];
  for (const field of fields) {
    if (!LABELS[field]) continue;
    const before = old[field];
    const after = field in now ? now[field] : entry.changes?.[field];
    if (same(before, after)) continue;
    lines.push({ field: LABELS[field], from: show(field, before, formatMoney), to: show(field, after, formatMoney) });
  }
  return lines;
}

const when = (iso: string, withTime: boolean) =>
  new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });

/** "Created 12 Sept 2026 by Mukhal · Last updated 3 Oct 2026, 10:42 am by Asha" */
export function editInfoLine(info: { created_at: string; created_by_name: string | null; updated_at: string; updated_by_name: string | null }): string {
  const by = (name: string | null) => (name ? ` by ${name}` : '');
  return `Created ${when(info.created_at, false)}${by(info.created_by_name)} · Last updated ${when(info.updated_at, true)}${by(info.updated_by_name)}`;
}
